import { useCallback, useEffect, useRef, useState } from "react";
import ResizeObserver from "resize-observer-polyfill";
// TODO: Refactor to reuse logic in OverflowScroller hook
export const useOverflowState = ({ overflowRef, contentRef, onScrollEndX, onScrollEndY }) => {
    const [hasOverflowTop, setHasOverflowTop] = useState(false);
    const [hasOverflowBottom, setHasOverflowBottom] = useState(false);
    const [hasOverflowLeft, setHasOverflowLeft] = useState(false);
    const [hasOverflowRight, setHasOverflowRight] = useState(false);
    const animationFrameRef = useRef(0);
    const callbackRefs = useRef({
        onScrollEndX,
        onScrollEndY,
        isScrolledEndX: false,
        isScrolledEndY: false
    });
    callbackRefs.current.onScrollEndX = onScrollEndX;
    callbackRefs.current.onScrollEndY = onScrollEndY;
    const updateOverflowState = useCallback(() => {
        const update = () => {
            if (!overflowRef.current) {
                return;
            }
            const { onScrollEndX, onScrollEndY } = callbackRefs.current;
            const { scrollHeight, clientHeight, scrollWidth, clientWidth, scrollTop, scrollLeft } = overflowRef.current;
            const hasVerticalOverflow = scrollHeight > clientHeight;
            const hasOverflowTop = hasVerticalOverflow && scrollTop > 0;
            // Math.ceil to prevent scrollTop getting stuck on a half pixel
            const hasScrolledEndY = Math.ceil(scrollTop) >= scrollHeight - clientHeight;
            const hasOverflowBottom = hasVerticalOverflow && !hasScrolledEndY;
            const hasHorizontalOverflow = scrollWidth > clientWidth;
            const hasOverflowLeft = hasHorizontalOverflow && scrollLeft > 0;
            // Math.ceil to prevent scrollLeft getting stuck on a half pixel
            const hasScrolledEndX = Math.ceil(scrollLeft) >= scrollWidth - clientWidth;
            const hasOverflowRight = hasHorizontalOverflow && !hasScrolledEndX;
            setHasOverflowTop(hasOverflowTop);
            setHasOverflowBottom(hasOverflowBottom);
            setHasOverflowLeft(hasOverflowLeft);
            setHasOverflowRight(hasOverflowRight);
            if (onScrollEndX && hasScrolledEndX) {
                if (!callbackRefs.current.isScrolledEndX) {
                    onScrollEndX();
                    callbackRefs.current.isScrolledEndX = true;
                }
            }
            else {
                callbackRefs.current.isScrolledEndX = false;
            }
            if (onScrollEndY && hasScrolledEndY) {
                if (!callbackRefs.current.isScrolledEndY) {
                    onScrollEndY();
                    callbackRefs.current.isScrolledEndY = true;
                }
            }
            else {
                callbackRefs.current.isScrolledEndY = false;
            }
        };
        if (process.env.NODE_ENV === "test") {
            // skip requestAnimationFrame in tests
            update();
            return;
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        // Delay measurements until next frame. Sometimes scrollWidth isn't correct until then
        animationFrameRef.current = requestAnimationFrame(update);
    }, [callbackRefs, overflowRef]);
    /* Update overflow on mount and resize */
    useEffect(() => {
        /* Skip ResizeObserver for tests */
        if (process.env.NODE_ENV === "test") {
            updateOverflowState();
            return;
        }
        const overflowElement = overflowRef.current;
        const contentElement = contentRef.current;
        const resizeObserver = new ResizeObserver(updateOverflowState);
        resizeObserver.observe(overflowElement);
        resizeObserver.observe(contentElement);
        updateOverflowState();
        return () => {
            resizeObserver.disconnect();
            cancelAnimationFrame(animationFrameRef.current);
        };
    }, [overflowRef, contentRef, updateOverflowState]);
    return {
        updateOverflowState,
        overflowState: {
            up: hasOverflowTop,
            down: hasOverflowBottom,
            left: hasOverflowLeft,
            right: hasOverflowRight
        }
    };
};
